import {Elements as StripeElementsProvider} from '@stripe/react-stripe-js';
import {FunctionComponent, PropsWithChildren, useCallback, useEffect, useMemo, useState} from "react";
import * as stripeJs from "@stripe/stripe-js";
import {useColorScheme, useTheme} from "@mui/material";
import Api from "../../../core/services/api/api";
import useIsMounted from "../use-is-mounted";
import useAccessLevels from "../use-access-levels";

interface StripeProviderProps {
    authToken: string;
    bypassAuthCheck?: boolean;
    fetchStripeKey?: (authorization: string) => Promise<{ resultFlag?: boolean, data?: { publicKey?: string } } | undefined>;
}

const StripeProvider: FunctionComponent<PropsWithChildren<StripeProviderProps>> = (props) => {
    const {fetchStripeKey: fetchStripeKeyFunc} = props;

    const [stripePublicKey, setStripePublicKey] = useState(null);
    const colorScheme = useColorScheme()
    const darkMode = useMemo(() => colorScheme.mode === 'dark' || (colorScheme.mode === 'system' && colorScheme.systemMode === 'dark'), [colorScheme]);
    const theme = useTheme();
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canManagePaymentMethods = packages.sales.manageCustomerPaymentMethods;

    /**
     * Fetches the stripe public key from the api.
     */
    const fetchStripeKey = useCallback(async (authorization) => {
        if (!props.bypassAuthCheck && !canManagePaymentMethods)
            return;
        const response = fetchStripeKeyFunc ? await fetchStripeKeyFunc(authorization) : await Api.getStripePublicKey(() => ({
            Authorization: authorization,
        }), props.bypassAuthCheck);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setStripePublicKey(response?.data?.publicKey);
        }
    }, [isMounted, props.bypassAuthCheck, canManagePaymentMethods, fetchStripeKeyFunc])

    /**
     * Fetches the stripe public key when the component is mounted.
     * - This is done to avoid fetching the stripe public key on every render.
     */
    useEffect(() => {
        if (!props.authToken)
            return;
        void fetchStripeKey(props.authToken);
    }, [fetchStripeKey, props.authToken])

    const stripePromise = useMemo(() => stripePublicKey && props.authToken
            ? stripeJs.loadStripe(stripePublicKey)
            : Promise.resolve(null)
        , [props.authToken, stripePublicKey]);

    const stripeOptions = useMemo<stripeJs.StripeElementsOptions>(() => ({
        fonts: [{
            cssSrc: "https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700;800&display=swap",
        }],
        loader: 'always',
        locale: 'auto',
        appearance: {
            theme: darkMode ? 'night' : 'stripe',
            labels: 'floating',
            variables: {
                ...(!props.bypassAuthCheck && {
                    // Colors
                    colorPrimary: theme.palette.primary.main,
                    colorBackground: theme.palette.background.paper,
                    colorText: theme.palette.text.primary,
                    colorSuccess: theme.palette.success.main,
                    colorDanger: theme.palette.error.main,
                    colorWarning: theme.palette.warning.main,

                    // Text variations
                    colorTextSecondary: theme.palette.text.secondary,
                    colorTextPlaceholder: theme.palette.text.secondary,

                    // Accessible text
                    accessibleColorOnColorPrimary: theme.palette.primary.contrastText,
                    accessibleColorOnColorBackground: theme.palette.text.primary,
                    accessibleColorOnColorSuccess: theme.palette.success.contrastText,
                    accessibleColorOnColorDanger: theme.palette.error.contrastText,
                    accessibleColorOnColorWarning: theme.palette.warning.contrastText,

                    // Icons
                    iconColor: theme.palette.primary.main,
                    iconHoverColor: theme.palette.primary.main,

                    // TabIcons
                    tabIconColor: theme.palette.text.primary,
                    tabIconHoverColor: theme.palette.secondary.main,
                    tabIconSelectedColor: theme.palette.primary.main,
                    tabIconMoreColor: theme.palette.text.primary,
                    tabIconMoreHoverColor: theme.palette.primary.main,

                    // Logos
                    logoColor: darkMode ? 'dark' : 'light',
                    tabLogoColor: darkMode ? 'dark' : 'light',
                    tabLogoSelectedColor: darkMode ? 'dark' : 'light',
                    blockLogoColor: darkMode ? 'dark' : 'light',

                    // Radius
                    borderRadius: `${theme.shape.borderRadius}px`,

                    focusBoxShadow: 'none',
                    focusOutline: 'none',
                }),
            },
        },
    }), [darkMode, props.bypassAuthCheck, theme.palette.background.paper, theme.palette.error.contrastText, theme.palette.error.main, theme.palette.primary.contrastText, theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.contrastText, theme.palette.success.main, theme.palette.text.primary, theme.palette.text.secondary, theme.palette.warning.contrastText, theme.palette.warning.main, theme.shape.borderRadius]);

    return (
        <StripeElementsProvider stripe={stripePromise} options={stripeOptions}>
            {props.children}
        </StripeElementsProvider>
    );
}

export default StripeProvider;